/**
 * STABILITY-CRITICAL: estado da tela do fluxo de saída/limpeza (passos, bloqueio, backup).
 * O botão "Salvar backup" é o gesto que abre o showSaveFilePicker; nada é apagado sem o backup
 * confirmado. Antes de alterar, leia docs/llm/slices/sync-engine.md e alinhe com o responsável.
 */
import {
  BackupAbortedError,
  saveByDownload,
  saveWithPicker,
} from './backupConfirmed'
import type { BackupFile } from './backupConfirmed'
import type { BackupRequest, BlockedInfo, FlowUi } from './safeguards'

export type FlowMode = 'saida' | 'limpeza'
export type FlowPhase = 'running' | 'blocked' | 'backup' | 'finishing' | 'error'
export type BackupView = {
  filename: string
  sizeKb: number
  farmsCount: number
  canUsePicker: boolean
  mustConfirm: boolean
  warnings: string[]
  saving: boolean
  downloaded: boolean
  confirmedChecked: boolean
  notice: string | null
}
export type FlowView = {
  mode: FlowMode
  phase: FlowPhase
  step: 1 | 2 | 3 | 4
  message: string | null
  blocked: BlockedInfo | null
  backup: BackupView | null
  error: string | null
}

export function createFlowUiController(
  mode: FlowMode,
  win: any = typeof window !== 'undefined' ? window : undefined,
) {
  let view: FlowView = {
    mode,
    phase: 'running',
    step: 1,
    message: null,
    blocked: null,
    backup: null,
    error: null,
  }
  let listeners: Array<() => void> = []
  const set = (patch: Partial<FlowView>) => {
    view = { ...view, ...patch }
    listeners.slice().forEach((l) => l())
  }
  const setBackup = (patch: Partial<BackupView>) => {
    if (view.backup) set({ backup: { ...view.backup, ...patch } })
  }

  let blockedResolve: ((c: 'retry' | 'cancel') => void) | null = null
  let backupHandlers: {
    resolve: (r: { method: 'picker' | 'download' }) => void
    reject: (e: Error) => void
  } | null = null
  let currentFile: BackupFile | null = null

  const ui: FlowUi = {
    setStep(step, message) {
      set({
        phase: 'running',
        step,
        message: message ?? null,
        blocked: null,
        backup: null,
      })
    },
    finishing() {
      set({
        phase: 'finishing',
        message: 'Finalizando...',
        blocked: null,
        backup: null,
      })
    },
    showBlocked(info) {
      set({ phase: 'blocked', blocked: info, backup: null })
      return new Promise((resolve) => {
        blockedResolve = resolve
      })
    },
    requestBackupSave(r: BackupRequest) {
      currentFile = r.file
      set({
        phase: 'backup',
        step: 2,
        blocked: null,
        backup: {
          filename: r.file.filename,
          sizeKb: r.file.sizeKb,
          farmsCount: r.file.farmsCount,
          canUsePicker: r.canUsePicker,
          mustConfirm: r.mustConfirm,
          warnings: r.warnings,
          saving: false,
          downloaded: false,
          confirmedChecked: false,
          notice: null,
        },
      })
      return new Promise((resolve, reject) => {
        backupHandlers = { resolve, reject }
      })
    },
  }

  const actions = {
    retry() {
      const r = blockedResolve
      blockedResolve = null
      set({ phase: 'running', blocked: null })
      r?.('retry')
    },
    cancel() {
      if (view.phase === 'blocked') {
        const r = blockedResolve
        blockedResolve = null
        r?.('cancel')
      } else if (view.phase === 'backup') {
        const h = backupHandlers
        backupHandlers = null
        h?.reject(new BackupAbortedError('cancelled'))
      }
    },
    /** Handler do botão [Salvar backup]: showSaveFilePicker TEM de ser a primeira chamada async. */
    async saveBackup() {
      if (!currentFile || !backupHandlers || view.backup?.saving) return
      setBackup({ saving: true, notice: null })
      try {
        const r = await saveWithPicker(currentFile, win)
        const h = backupHandlers
        backupHandlers = null
        h?.resolve({ method: r.method })
      } catch (e) {
        const reason =
          e instanceof BackupAbortedError ? e.reason : 'picker_failed'
        setBackup({
          saving: false,
          notice:
            reason === 'cancelled'
              ? 'Você cancelou a escolha do arquivo. Clique em "Salvar backup" para tentar de novo ou cancele a operação. Nada foi apagado.'
              : `Não foi possível gravar o arquivo (${(e as Error)?.message ?? 'erro'}). Tente de novo ou cancele a operação. Nada foi apagado.`,
        })
      }
    },
    /** Fallback sem showSaveFilePicker. */
    downloadBackup() {
      if (!currentFile) return
      saveByDownload(currentFile)
      setBackup({ downloaded: true })
    },
    setConfirmed(checked: boolean) {
      setBackup({ confirmedChecked: checked })
    },
    continueAfterDownload() {
      if (!view.backup?.downloaded || !view.backup.confirmedChecked) return
      const h = backupHandlers
      backupHandlers = null
      h?.resolve({ method: 'download' })
    },
    fail(message: string) {
      set({ phase: 'error', error: message, blocked: null, backup: null })
    },
  }

  return {
    ui,
    actions,
    getView: () => view,
    subscribe: (l: () => void) => {
      listeners.push(l)
      return () => {
        listeners = listeners.filter((x) => x !== l)
      }
    },
  }
}
export type FlowUiController = ReturnType<typeof createFlowUiController>
